import { EoReader, EoWriter, PacketAction, PacketFamily } from 'eolib';
import type { Client } from '@/client';
import { EquipmentSlot } from '@/equipment';
import {
  FAMILY_FORGE,
  FORGE_ACTION_ADD_ELEMENT,
  FORGE_ACTION_ADD_RANDOM_STAT,
  FORGE_ACTION_REMOVE_ELEMENT,
  FORGE_ACTION_STATUS,
  FORGE_ACTION_UPGRADE,
  FORGE_ACTION_RECYCLE,
  FORGE_ACTION_RESET_STATS,
  FORGE_SLOT_CODES,
  type ForgeSlotKey,
} from '@/forge-protocol';

export interface ForgeElementMapping {
  item_id: number;
  element: number;
  power: number;
  label?: string;
  element_name?: string;
}

export interface ForgeUpgradeRule {
  target_level: number;
  success_percent: number;
  fail_reset_level: number;
  gold_cost: number;
  luna_gem_cost: number;
}

export interface ForgeStatGemMapping {
  item_id: number;
  option_types: string[];
  min_value: number;
  max_value: number;
  tier: number;
  label?: string;
}

export interface ForgeOpenPayload {
  title?: string;
  message?: string;
  ron_npc_id?: number;
  shop_behavior_id?: number;
  luna_gem_item_id?: number;
  max_upgrade_level?: number;
  protocol_version?: number;
  recycling_enabled?: boolean;
  stat_reset_gold_cost?: number;
  stat_socket_count?: number;
  crystal_success_bonus_percent?: number;
  crystal_max_count?: number;
  failure_wipe_percent?: number;
  safe_upgrade_checkpoint?: number;
  weapon_max_element_slots?: number;
  second_element_unlock_level?: number;
  element_add_gold_cost?: number;
  element_remove_gold_cost?: number;
  random_option_success_percent?: number;
  stat_gem_gold_cost?: number;
  stat_gem_base_success_percent?: number;
  stat_gem_success_bonus_percent?: number;
  stat_gem_max_success_percent?: number;
  stat_gem_max_count?: number;
  element_mappings?: ForgeElementMapping[];
  stat_gem_mappings?: ForgeStatGemMapping[];
  upgrade_rules?: ForgeUpgradeRule[];
}

export interface ForgeItemElement {
  slot: number;
  element: string;
  power: number;
  source_item_id?: number;
}

export interface ForgeItemEnhancement {
  weapon_effects?: import('@/utils/weapon-effect-lines').WeaponEffectProfile[];
  id: number;
  instance_id?: number;
  location?: string;
  owner_player_id?: number;
  totals?: string[];
  options?: {stat:string;value:number}[];
  stat_sockets_used?: number;
  stat_sockets_max?: number;
  name: string;
  display_name: string;
  slot?: string;
  slot_label?: string;
  upgrade_level: number;
  equipped?: boolean;
  elements: ForgeItemElement[];
  stats: string[];
}

export interface ForgeReply {
  success: boolean;
  message: string;
  item?: ForgeItemEnhancement;
}

export type ForgeDropSlot = 'target' | 'material';

type OpenedSubscriber = (payload: ForgeOpenPayload) => void;
type ReplySubscriber = (reply: ForgeReply) => void;
type DropSubscriber = (slot: ForgeDropSlot, itemId: number) => void;

export class ForgeController {
  private client: Client;
  sessionId = 0;
  private mutationPending = false;
  openData: ForgeOpenPayload | null = null;
  messages: ForgeReply[] = [];
  private itemEnhancements = new Map<number, ForgeItemEnhancement>();
  private ownedEnhancements: ForgeItemEnhancement[] = [];
  private tradeEnhancements: ForgeItemEnhancement[] = [];
  private syncTimer: ReturnType<typeof setTimeout> | undefined;

  requestOwnedSync(): void {
    clearTimeout(this.syncTimer);
    this.syncTimer = setTimeout(() => {
      if (this.client.state !== 7 || !this.client.bus) return;
      this.client.bus.sendBuf(FAMILY_FORGE as PacketFamily, PacketAction.List, new Uint8Array());
    }, 150);
  }

  handleOwnedSync(reader: EoReader): void {
    try {
      const payload = JSON.parse(reader.getString());
      if (!Array.isArray(payload.items)) return;
      if(payload.scope==='trade') {this.tradeEnhancements=payload.items;window.dispatchEvent(new Event('forge-items-updated'));return;}
      this.ownedEnhancements = payload.items;
      this.itemEnhancements.clear();
      this.client.inventoryController.refreshEnhancementDisplay();
      window.dispatchEvent(new Event('forge-items-updated'));
    } catch (error) { console.error('Invalid owned forge metadata', error); }
  }

  clearOwnedSync(): void {
    this.mutationPending=false;
    this.sessionId=0;
    clearTimeout(this.syncTimer);
    this.ownedEnhancements = [];
    this.tradeEnhancements = [];
    this.itemEnhancements.clear();
  }

  private openedSubscribers: OpenedSubscriber[] = [];
  private replySubscribers: ReplySubscriber[] = [];
  private dropSubscribers: DropSubscriber[] = [];

  constructor(client: Client) {
    this.client = client;
  }

  subscribeOpened(cb: OpenedSubscriber): void {
    this.openedSubscribers.push(cb);
  }

  unsubscribeOpened(cb: OpenedSubscriber): void {
    this.openedSubscribers = this.openedSubscribers.filter((s) => s !== cb);
  }

  subscribeReply(cb: ReplySubscriber): void {
    this.replySubscribers.push(cb);
  }

  unsubscribeReply(cb: ReplySubscriber): void {
    this.replySubscribers = this.replySubscribers.filter((s) => s !== cb);
  }

  subscribeDrop(cb: DropSubscriber): void {
    this.dropSubscribers.push(cb);
  }

  unsubscribeDrop(cb: DropSubscriber): void {
    this.dropSubscribers = this.dropSubscribers.filter((s) => s !== cb);
  }

  dropItem(slot: ForgeDropSlot, itemId: number): void {
    for (const cb of this.dropSubscribers) cb(slot, itemId);
  }

  getStoredEnhancement(itemId:number,location:string):ForgeItemEnhancement|undefined {
    return this.ownedEnhancements.filter(i=>i.id===itemId && i.location===location).sort((a,b)=>b.upgrade_level-a.upgrade_level || (b.elements?.length??0)-(a.elements?.length??0) || (b.options?.length??0)-(a.options?.length??0) || (a.instance_id??0)-(b.instance_id??0))[0];
  }
  getTradeEnhancements(itemId:number,owner:number):ForgeItemEnhancement[] {
    return this.tradeEnhancements.filter(i=>i.id===itemId && i.owner_player_id===owner);
  }
  getForgeTargetEnhancement(itemId:number):ForgeItemEnhancement | undefined {
    return this.getItemEnhancement(itemId,this.client.inventoryController.getItemAmount(itemId)<=0);
  }
  getItemEnhancement(itemId: number, equipped?: boolean): ForgeItemEnhancement | undefined {
    const matching = this.ownedEnhancements.filter(i => i.id === itemId && (equipped === undefined ? i.location !== "bank" : equipped ? i.equipped === true : i.location === "inventory"));
    return matching.sort((a,b) => b.upgrade_level-a.upgrade_level || (b.elements?.length??0)-(a.elements?.length??0) || (b.options?.length??0)-(a.options?.length??0) || (a.instance_id??0)-(b.instance_id??0))[0]
      ?? (equipped === undefined ? this.itemEnhancements.get(itemId) : undefined);
  }

  open(npcIndex: number): void {
    const writer = new EoWriter();
    writer.addShort(npcIndex);
    this.client.bus!.sendBuf(
      FAMILY_FORGE as PacketFamily,
      PacketAction.Open,
      writer.toByteArray(),
    );
  }

  status(targetItemId: number): void {
    this.use(FORGE_ACTION_STATUS, 'weapon', 0, targetItemId);
  }

  upgrade(slot: ForgeSlotKey, targetItemId: number, crystalId=0, crystalCount=0): void {
    this.use(FORGE_ACTION_UPGRADE, slot, 0, targetItemId, 1, crystalId, crystalCount);
  }

  addElement(slot: ForgeSlotKey, targetItemId: number, gemId: number): void {
    this.use(FORGE_ACTION_ADD_ELEMENT, slot, gemId, targetItemId);
  }

  addRandomStat(
    slot: ForgeSlotKey,
    targetItemId: number,
    gemId: number,
    gemCount: number,
    crystalId=0, crystalCount=0,
  ): void {
    this.use(FORGE_ACTION_ADD_RANDOM_STAT, slot, gemId, targetItemId, gemCount, crystalId, crystalCount);
  }

  removeElement(slot: ForgeSlotKey, targetItemId: number): void {
    this.use(FORGE_ACTION_REMOVE_ELEMENT, slot, 0, targetItemId);
  }

  handleOpened(reader: EoReader): void {
    this.sessionId = reader.getShort();
    this.client.sessionId = this.sessionId;

    const rawPayload = reader.getString();
    let payload: ForgeOpenPayload = {
      title: 'Ron The Smith Forge',
      message: 'Ron The Smith: choose a forge service.',
    };

    if (rawPayload) {
      try {
        payload = { ...payload, ...JSON.parse(rawPayload) };
      } catch (err) {
        console.error('Failed to parse forge open payload', err, rawPayload);
      }
    }

    this.openData = payload;
    this.messages = payload.message
      ? [{ success: true, message: payload.message }]
      : [];

    for (const cb of this.openedSubscribers) cb(payload);
  }

  handleReply(reader: EoReader): void {
    this.mutationPending=false;
    const success = reader.getChar() === 1;
    const rawMessage = reader.getString();
    let message = rawMessage;
    let item: ForgeItemEnhancement | undefined;

    if (rawMessage.trim().startsWith('{')) {
      try {
        const payload = JSON.parse(rawMessage) as {
          message?: string;
          item?: ForgeItemEnhancement;
        };
        message = payload.message || rawMessage;
        if (payload.item?.id) {
          item = payload.item;
          this.itemEnhancements.set(item.id, item);
          this.applyReturnedItem(item);
        }
      } catch (err) {
        console.error('Failed to parse forge reply payload', err, rawMessage);
      }
    }

    this.requestOwnedSync();
    const reply = { success, message, item };
    this.messages = [...this.messages.slice(-5), reply];

    if (success) {
      this.client.toastController.showSuccess(message);
    } else {
      this.client.toastController.showWarning(message);
    }

    for (const cb of this.replySubscribers) cb(reply);
  }

  private applyReturnedItem(item: ForgeItemEnhancement): void {
    if (!item.equipped) {
      return;
    }

    const slot = equipmentSlotFromForgeSlot(item.slot);
    if (slot === null) {
      return;
    }

    this.client.inventoryController.setEquipmentSlot(slot, item.id);
    this.client.inventoryController.notifyEquipmentChanged();

    if (this.client.inventoryController.isVisibleEquipmentChange(slot)) {
      const record = this.client.getEifRecordById(item.id);
      this.client.inventoryController.setNearbyCharacterEquipment(
        this.client.playerId,
        slot,
        record?.spec1 ?? item.id,
      );
    }
  }

  recycle(slot: ForgeSlotKey, targetItemId: number): void {
    this.use(FORGE_ACTION_RECYCLE, slot, 0, targetItemId);
  }

  resetStats(slot: ForgeSlotKey, targetItemId: number): void {
    this.use(FORGE_ACTION_RESET_STATS, slot, 0, targetItemId);
  }

  private use(
    actionCode: number,
    slot: ForgeSlotKey,
    materialItemId: number,
    targetItemId: number,
    materialCount = 1,
    crystalId=0, crystalCount=0,
  ): void {
    if (!this.sessionId) {
      this.client.toastController.showWarning('Talk to Ron The Smith first.');
      return;
    }

    if (this.mutationPending) {
      this.client.toastController.showWarning('Wait for the current forge result before trying again.');
      return;
    }
    if(actionCode!==FORGE_ACTION_STATUS)this.mutationPending=true;
    const writer = new EoWriter();
    writer.addShort(this.sessionId);
    writer.addChar(actionCode);
    writer.addChar(FORGE_SLOT_CODES[slot]);
    writer.addShort(materialItemId);
    writer.addShort(targetItemId);
    writer.addChar(Math.max(1, Math.min(250, Math.floor(materialCount))));
    writer.addShort(crystalId);
    writer.addChar(Math.max(0,Math.min(25,Math.floor(crystalCount))));
    writer.addShort(this.getForgeTargetEnhancement(targetItemId)?.upgrade_level ?? Number(this.client.getEifRecordById(targetItemId)?.name.match(/\+(\d+)$/)?.[1] || 0));
    const selected=this.getForgeTargetEnhancement(targetItemId);
    writer.addInt(selected?.instance_id??0);
    writer.addShort(selected?.options?.length??0);
    // Baked-in legacy elements are part of the base record, not socket rows.
    writer.addChar(selected?.instance_id ? (selected.elements?.filter(e=>e.source_item_id!==0).length??0) : 0);

    if (actionCode === FORGE_ACTION_RECYCLE) writer.addInt(this.client.inventoryController.getItemAmount(targetItemId));
    if (actionCode === FORGE_ACTION_RESET_STATS) writer.addInt(this.openData?.stat_reset_gold_cost ?? 0);

    this.client.bus!.sendBuf(
      FAMILY_FORGE as PacketFamily,
      PacketAction.Use,
      writer.toByteArray(),
    );
  }
}

function equipmentSlotFromForgeSlot(slot?: string): EquipmentSlot | null {
  switch (slot) {
    case 'weapon':
      return EquipmentSlot.Weapon;
    case 'armor':
      return EquipmentSlot.Armor;
    case 'shield':
      return EquipmentSlot.Shield;
    case 'hat':
      return EquipmentSlot.Hat;
    case 'boots':
      return EquipmentSlot.Boots;
    case 'gloves':
      return EquipmentSlot.Gloves;
    default:
      return null;
  }
}
